/** Raw transport heartbeat handled by the ConnDO Hibernation auto-response. */
export const HEARTBEAT_REQUEST = "ping";
export const HEARTBEAT_RESPONSE = "pong";
export const HEARTBEAT_CLOSE_CODE = 4000;
export const HEARTBEAT_CLOSE_REASON = "HEARTBEAT_TIMEOUT";

export const DEFAULT_HEARTBEAT_INTERVAL_MS = 25_000;
export const DEFAULT_HEARTBEAT_TIMEOUT_MS = 10_000;

export interface HeartbeatSettings {
  readonly intervalMs: number;
  readonly timeoutMs: number;
}

/**
 * Validates heartbeat timing at the SDK boundary.
 *
 * The timeout is intentionally shorter than the interval so at most one
 * heartbeat can be in flight. Values are bounded to prevent accidental hot
 * loops or timers too long to detect a broken mobile connection usefully.
 */
export function heartbeatSettings(
  intervalMs = DEFAULT_HEARTBEAT_INTERVAL_MS,
  timeoutMs = DEFAULT_HEARTBEAT_TIMEOUT_MS,
): HeartbeatSettings {
  if (!Number.isFinite(intervalMs) || intervalMs < 1_000 || intervalMs > 120_000) {
    throw new RangeError("heartbeatIntervalMs must be between 1000 and 120000");
  }
  if (!Number.isFinite(timeoutMs) || timeoutMs < 1_000 || timeoutMs >= intervalMs) {
    throw new RangeError("heartbeatTimeoutMs must be between 1000 and heartbeatIntervalMs");
  }
  return { intervalMs, timeoutMs };
}

/**
 * Runs a single-flight heartbeat without owning the WebSocket lifecycle.
 *
 * A response schedules the next probe. A missing response stops the monitor
 * and delegates connection teardown to the owner, preventing duplicate
 * reconnect attempts from competing timers.
 */
export class HeartbeatMonitor {
  private probeTimer: ReturnType<typeof setTimeout> | null = null;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;

  public constructor(
    private readonly settings: HeartbeatSettings,
    private readonly sendProbe: () => boolean,
    private readonly onTimeout: () => void,
  ) {}

  public start(): void {
    this.stop();
    this.scheduleProbe();
  }

  /** Returns true only when the response completed an in-flight probe. */
  public acknowledge(): boolean {
    if (this.timeoutTimer === null) return false;
    clearTimeout(this.timeoutTimer);
    this.timeoutTimer = null;
    this.scheduleProbe();
    return true;
  }

  public stop(): void {
    if (this.probeTimer !== null) clearTimeout(this.probeTimer);
    if (this.timeoutTimer !== null) clearTimeout(this.timeoutTimer);
    this.probeTimer = null;
    this.timeoutTimer = null;
  }

  private scheduleProbe(): void {
    if (this.probeTimer !== null) clearTimeout(this.probeTimer);
    this.probeTimer = setTimeout(() => {
      this.probeTimer = null;
      if (!this.sendProbe()) {
        this.onTimeout();
        return;
      }
      this.timeoutTimer = setTimeout(() => {
        this.timeoutTimer = null;
        this.onTimeout();
      }, this.settings.timeoutMs);
    }, this.settings.intervalMs);
  }
}
